import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useLocation } from 'react-router-dom';
import { useIsMutating, useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { Check, Copy, Info, KeyRound, Trash2 } from 'lucide-react';
import {
  SOCIAL_CREDENTIAL_SETS,
  SOCIAL_CREDENTIAL_SET_INFO,
  bothKeysRequired,
  type SaveSocialAppKeysDto,
  type SocialAppKeysEntry,
  type SocialAppKeysView,
  type SocialCredentialSet,
} from '@mg/shared';
import { api, ApiRequestError } from '../../lib/api';
import { Card, CardBody, CardHeader } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Label } from '../../components/ui/Field';
import { useFieldId } from '../../components/ui/useFieldId';
import { useDialogs } from '../../components/ui/DialogProvider';
import { CHANGES_SAVED, useToast } from '../../components/ui/toast';
import { useSaveHold } from '../../components/settings/footerSave';
import {
  SOCIAL_APPS_ANCHOR,
  SOCIAL_APP_KEYS_STATUS_KEY,
  SOCIAL_APP_KEYS_VIEW_KEY,
  scrollToSocialApps,
} from '../social/socialAppKeys';
import { SetupLink } from './integrationUi';
import { KEY_INPUT_CLASS, SECRET_MASK, URL_INPUT_CLASS, nothingToReplace } from './socialMediaIntegrations';

/** Every row's save runs under this key plus its set, so the card can tell while any of them is running. */
const SAVE_KEY = ['social-app-keys', 'save'] as const;

/**
 * Site Integrations → Social Media Integrations: the install's own social developer apps, one row per
 * credential set (LinkedIn has two apps, one per connection type; Instagram signs in through the
 * Facebook one). A Tenant Admin adds, replaces or removes each app's client ID and secret here, and
 * every new connection and every Reconnect on the install then signs in with them. Adding keys
 * connects no account: each user still signs in to their own from the Connections page.
 *
 * The secret is write-only. The server says only whether one is saved (`secretSet`), so a saved one is
 * shown as a fixed run of bullets and the secret field always starts empty. Install-wide and
 * Tenant-Admin-only, like the rest of Site Integrations, whose page checks the role before mounting
 * this card; its endpoints check it again.
 */
export function SocialMediaIntegrationsCard() {
  const query = useQuery({
    queryKey: SOCIAL_APP_KEYS_VIEW_KEY,
    queryFn: ({ signal }) => api.getSocialAppKeys(signal),
  });
  // One form open at a time, held here: opening a row closes the open one and drops what was typed in it.
  const [openSet, setOpenSet] = useState<SocialCredentialSet | null>(null);
  // A save's outcome shows in its form, and a form closed mid-save never hears the answer: a failure
  // would go unseen, and a secret-only Replace would look saved. So while any save runs, every toggle
  // is disabled (opening another row closes the open form too), as the form's own Cancel is. Leaving
  // the tab or the page waits for it as well: each save and removal is handed to the page (useSaveHold).
  const saving = useIsMutating({ mutationKey: SAVE_KEY }) > 0;

  // Arriving from "Open Site Integrations" (`/site-integrations#social-media-integrations`): a
  // client-side route change does not scroll to a hash, and the layout's scroller keeps its position.
  // Scroll here, and again once the rows are in and the card has its full height.
  const { hash } = useLocation();
  useEffect(() => {
    if (hash !== `#${SOCIAL_APPS_ANCHOR}`) return;
    const frame = requestAnimationFrame(scrollToSocialApps);
    return () => cancelAnimationFrame(frame);
  }, [hash, query.isSuccess]);

  // An answer without every set (an API of another version mid-deploy) reads as a failed load, never
  // as "Not set" and never as a crash that would take the page's other cards down with it.
  const view = query.isSuccess && hasEverySet(query.data) ? query.data : null;

  return (
    <Card id={SOCIAL_APPS_ANCHOR} className="scroll-mt-24">
      <CardHeader
        title="Social Media Integrations"
        description="App credentials Smorgas Social uses to connect your social accounts. Adding keys here doesn't connect an account: each user still signs in to their own account from the Connections page."
      />
      <CardBody className="space-y-4">
        {query.isPending ? (
          <p className="text-sm text-ink-muted">Loading social media integrations…</p>
        ) : view === null ? (
          <p className="text-sm text-red-600 dark:text-error-dark">Could not load your social media integrations.</p>
        ) : (
          <ul className="space-y-3">
            {SOCIAL_CREDENTIAL_SETS.map((set) => (
              <SocialAppRow
                key={set}
                set={set}
                entry={view[set]}
                open={openSet === set}
                toggleDisabled={saving}
                onToggle={() => setOpenSet((current) => (current === set ? null : set))}
                onClose={() => setOpenSet((current) => (current === set ? null : current))}
              />
            ))}
          </ul>
        )}
      </CardBody>
    </Card>
  );
}

function hasEverySet(view: SocialAppKeysView | undefined): view is SocialAppKeysView {
  return (
    typeof view === 'object' &&
    view !== null &&
    SOCIAL_CREDENTIAL_SETS.every((set) => typeof view[set] === 'object' && view[set] !== null)
  );
}

/**
 * After a save or a removal. The server answers with the whole new view, which replaces the cached
 * one. The Connections page's gate (the key status) and the "Recent site changes" log ask again.
 */
function applySaved(queryClient: QueryClient, view: SocialAppKeysView): void {
  queryClient.setQueryData(SOCIAL_APP_KEYS_VIEW_KEY, view);
  void queryClient.invalidateQueries({ queryKey: SOCIAL_APP_KEYS_STATUS_KEY });
  void queryClient.invalidateQueries({ queryKey: ['site-audit'] });
}

function SocialAppRow({
  set,
  entry,
  open,
  toggleDisabled,
  onToggle,
  onClose,
}: {
  set: SocialCredentialSet;
  entry: SocialAppKeysEntry;
  open: boolean;
  toggleDisabled: boolean;
  onToggle: () => void;
  onClose: () => void;
}) {
  const info = SOCIAL_CREDENTIAL_SET_INFO[set];
  const queryClient = useQueryClient();
  const { confirm, alert } = useDialogs();
  const toast = useToast();
  const hold = useSaveHold();
  const toggleRef = useRef<HTMLButtonElement>(null);
  const formId = useFieldId('social-app-keys-form');
  // "Key set" means BOTH values are saved: an ID alone, or a secret alone, is still "Not set".
  const keySet = entry.keySet;

  // Cancel, and a save that went through, close the form and give focus back to the toggle once the
  // render that closes it is done: by then the toggle is enabled again. A save's own callback runs while
  // the toggle is still drawn disabled for that save, and a disabled button takes no focus, so focus
  // moved from there would be lost along with the form.
  const focusToggleOnClose = useRef(false);
  useEffect(() => {
    if (open || !focusToggleOnClose.current) return;
    focusToggleOnClose.current = false;
    toggleRef.current?.focus();
  }, [open]);
  const closeForm = () => {
    focusToggleOnClose.current = true;
    onClose();
  };

  const remove = useMutation({
    mutationFn: () => api.removeSocialAppKeys(set),
    // Here rather than in the mutate() call, so it still confirms if the row has gone by then.
    onSuccess: (view) => {
      applySaved(queryClient, view);
      toast.show(CHANGES_SAVED);
    },
  });

  const onRemove = async () => {
    const confirmed = await confirm({
      title: 'Remove keys',
      message: `Remove the ${info.rowName} keys? Until keys are added again, nobody can start a new connection or reconnect an account that uses them. Accounts already connected keep working.`,
      confirmLabel: 'Remove',
      danger: true,
    });
    if (!confirmed) return;
    void hold(remove.mutateAsync()).then(
      // The strip and its Remove button are gone now: keep focus on the row.
      () => toggleRef.current?.focus(),
      (err: unknown) =>
        void alert({
          title: 'Something went wrong',
          message: err instanceof ApiRequestError ? err.message : 'Could not remove these keys.',
        }),
    );
  };

  return (
    <li className="rounded-lg border border-line px-4 py-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h4 className="text-sm font-medium text-ink">{info.rowName}</h4>
            {keySet ? (
              // An outline chip: green text on the card colour is 5.01:1 (7.2:1 dark); on the green tint it would be 4.21:1.
              <span className="inline-flex items-center gap-1 rounded-full border border-success/40 bg-card px-2 py-0.5 text-xs font-medium text-success dark:border-success-dark/40 dark:bg-success-dark-tint dark:text-success-dark">
                <Check className="h-3.5 w-3.5" aria-hidden /> Key set
              </span>
            ) : (
              <span className="inline-flex items-center rounded-full bg-chip px-2 py-0.5 text-xs font-medium text-ink-muted">
                Not set
              </span>
            )}
          </div>
          {info.subtext && <p className="mt-0.5 text-xs text-ink-muted">{info.subtext}</p>}
        </div>
        {/* The label says what the form will do; aria-expanded says whether it is open. */}
        <Button
          ref={toggleRef}
          variant="secondary"
          size="sm"
          aria-expanded={open}
          aria-controls={formId}
          disabled={toggleDisabled}
          onClick={onToggle}
        >
          <KeyRound className="h-4 w-4" aria-hidden />
          {keySet ? 'Replace keys' : 'Add keys'}
        </Button>
      </div>

      {info.footnote && <p className="mt-2 text-xs text-ink-muted">{info.footnote}</p>}

      {keySet && !open && (
        <div className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-md bg-page px-3 py-2">
          <dl className="min-w-0 space-y-0.5 text-xs">
            <div className="flex flex-wrap gap-x-1.5">
              <dt className="font-medium text-ink-strong">{info.idLabel}</dt>
              <dd className="break-all text-ink-label">{entry.clientId}</dd>
            </div>
            <div className="flex flex-wrap gap-x-1.5">
              <dt className="font-medium text-ink-strong">{info.secretLabel}</dt>
              {/* No reveal control anywhere: the browser never has the secret to reveal. */}
              <dd className="text-ink-label">
                <span aria-hidden="true">{SECRET_MASK}</span>
                <span className="sr-only">saved, not shown</span>
              </dd>
            </div>
          </dl>
          {/* red-600 on the page colour is 4.52:1; on hover, red-700 on red-50 is 5.91:1 (red-600 there: 4.41:1). */}
          <Button
            variant="ghost"
            size="sm"
            loading={remove.isPending}
            onClick={() => void onRemove()}
            className="text-red-600 dark:text-error-dark hover:bg-red-50 hover:text-red-700 dark:hover:bg-error-dark-tint"
          >
            <Trash2 className="h-4 w-4" aria-hidden /> Remove
          </Button>
        </div>
      )}

      {/* Always rendered, so the toggle's aria-controls always names an element. */}
      <div id={formId}>
        {open && <SocialAppForm set={set} entry={entry} onClose={closeForm} />}
      </div>
    </li>
  );
}

function SocialAppForm({
  set,
  entry,
  onClose,
}: {
  set: SocialCredentialSet;
  entry: SocialAppKeysEntry;
  /** Closes the form; the row then gives focus back to its toggle. */
  onClose: () => void;
}) {
  const info = SOCIAL_CREDENTIAL_SET_INFO[set];
  const keySet = entry.keySet;
  const queryClient = useQueryClient();
  const toast = useToast();
  const hold = useSaveHold();
  // A Replace starts from the saved ID (it is not a secret); an Add starts empty.
  const [clientId, setClientId] = useState(keySet ? (entry.clientId ?? '') : '');
  // Always starts empty: the browser never has the saved secret, and the bullets are only a placeholder.
  const [secret, setSecret] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [invalid, setInvalid] = useState({ clientId: false, secret: false });
  const [copied, setCopied] = useState(false);
  const copiedTimer = useRef<number | undefined>(undefined);
  const firstField = useRef<HTMLInputElement>(null);
  const secretField = useRef<HTMLInputElement>(null);

  const titleId = useFieldId('social-app-keys-title');
  const idId = useFieldId('social-app-keys-id');
  const idHelpId = useFieldId('social-app-keys-id-help');
  const secretId = useFieldId('social-app-keys-secret');
  const secretHelpId = useFieldId('social-app-keys-secret-help');
  const urlId = useFieldId('social-app-keys-url');
  const errorId = useFieldId('social-app-keys-error');

  // Opening the form moves focus to its first field.
  useEffect(() => {
    firstField.current?.focus();
  }, []);
  useEffect(() => () => window.clearTimeout(copiedTimer.current), []);

  const save = useMutation({
    mutationKey: [...SAVE_KEY, set],
    mutationFn: (dto: SaveSocialAppKeysDto) => api.saveSocialAppKeys(set, dto),
    // Here rather than in the mutate() call, so it still confirms if the form has gone by then.
    onSuccess: (view) => {
      applySaved(queryClient, view);
      toast.show(CHANGES_SAVED);
    },
  });

  const edited = () => {
    setError(null);
    setInvalid({ clientId: false, secret: false });
  };

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (save.isPending) return;
    const typedId = clientId.trim();
    const typedSecret = secret.trim();
    let dto: SaveSocialAppKeysDto;
    if (!keySet) {
      // Add: both values are needed before anything is sent.
      if (!typedId || !typedSecret) {
        setError(bothKeysRequired(set));
        setInvalid({ clientId: !typedId, secret: !typedSecret });
        (typedId ? secretField : firstField).current?.focus();
        return;
      }
      dto = { clientId: typedId, clientSecret: typedSecret };
    } else {
      // Replace: a blank field keeps its saved value, so it is left out (never sent as '' or null),
      // and so is an ID that did not change.
      dto = {
        ...(typedId && typedId !== entry.clientId ? { clientId: typedId } : {}),
        ...(typedSecret ? { clientSecret: typedSecret } : {}),
      };
      if (dto.clientId === undefined && dto.clientSecret === undefined) {
        setError(nothingToReplace(set));
        return;
      }
    }
    setError(null);
    void hold(save.mutateAsync(dto)).then(
      () => {
        setSecret('');
        onClose();
      },
      // The server's words, which never contain a value; the field keeps what was typed.
      (err: unknown) => setError(err instanceof ApiRequestError ? err.message : 'Could not save these keys.'),
    );
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(entry.redirectUri);
      setCopied(true);
      window.clearTimeout(copiedTimer.current);
      copiedTimer.current = window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // No clipboard here (an insecure origin, a denied permission): the field selects itself on focus.
    }
  };

  const describedBy = (help: string, isInvalid: boolean) => (isInvalid ? `${help} ${errorId}` : help);

  return (
    <form
      noValidate
      aria-labelledby={titleId}
      onSubmit={submit}
      className="mt-3 space-y-3 border-t border-line-soft pt-3"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p id={titleId} className="text-sm font-semibold text-ink">
          {keySet ? 'Replace' : 'Add'} {info.name} keys
        </p>
        <SetupLink href={info.guidePath}>Where do I get these?</SetupLink>
      </div>

      {info.infoNote && (
        <p className="flex gap-2 rounded-lg border border-line bg-tint/70 px-3 py-2 text-xs text-ink-strong">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-tint-fg" aria-hidden />
          {info.infoNote}
        </p>
      )}

      <div>
        <Label htmlFor={idId}>{info.idLabel}</Label>
        <input
          ref={firstField}
          id={idId}
          type="text"
          autoComplete="off"
          spellCheck={false}
          autoCapitalize="off"
          className={KEY_INPUT_CLASS}
          value={clientId}
          onChange={(e) => {
            edited();
            setClientId(e.target.value);
          }}
          placeholder={info.idPlaceholder}
          aria-describedby={describedBy(idHelpId, invalid.clientId)}
          aria-invalid={invalid.clientId || undefined}
        />
        <p id={idHelpId} className="mt-1 text-xs text-ink-muted">
          From your app&apos;s configuration page. Not a secret, so it&apos;s shown in full once saved.
        </p>
      </div>

      <div>
        <Label htmlFor={secretId}>{info.secretLabel}</Label>
        <input
          ref={secretField}
          id={secretId}
          type="password"
          autoComplete="new-password"
          className={KEY_INPUT_CLASS}
          value={secret}
          onChange={(e) => {
            edited();
            setSecret(e.target.value);
          }}
          placeholder={entry.secretSet ? SECRET_MASK : info.secretPlaceholder}
          aria-describedby={describedBy(secretHelpId, invalid.secret)}
          aria-invalid={invalid.secret || undefined}
        />
        <p id={secretHelpId} className="mt-1 text-xs text-ink-muted">
          Kept on our server (write-only, never shown again) and used only to complete the {info.signInName} sign-in.
          {keySet ? ' Leave blank to keep the current secret.' : ''}
        </p>
      </div>

      <div className="rounded-lg bg-page px-3 py-2.5">
        <label htmlFor={urlId} className="text-xs font-medium text-ink-label">
          Add this exact URL to your app under <span className="font-semibold">{info.consolePath}</span>:
        </label>
        <div className="mt-1.5 flex items-center gap-2">
          {/* The server builds it with the same function its OAuth flow sends: never guessed from this page's host. */}
          <input
            id={urlId}
            readOnly
            value={entry.redirectUri}
            onFocus={(e) => e.currentTarget.select()}
            className={URL_INPUT_CLASS}
          />
          <Button type="button" variant="secondary" size="sm" onClick={() => void copy()}>
            {copied ? (
              <Check className="h-4 w-4 text-success dark:text-success-dark" aria-hidden />
            ) : (
              <Copy className="h-4 w-4" aria-hidden />
            )}
            {copied ? 'Copied' : 'Copy'}
          </Button>
          <span role="status" className="sr-only">
            {copied ? 'Copied to the clipboard' : ''}
          </span>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Button type="submit" size="sm" loading={save.isPending}>
          Save
        </Button>
        <Button type="button" variant="ghost" size="sm" onClick={onClose} disabled={save.isPending}>
          Cancel
        </Button>
      </div>

      {/* Mounted before any error, so a screen reader announces the one that appears in it. */}
      <div role="alert">
        {error && (
          <p id={errorId} className="text-sm text-red-600 dark:text-error-dark">
            {error}
          </p>
        )}
      </div>
    </form>
  );
}
